import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FiCheckCircle } from 'react-icons/fi';
import { useQueryClient } from 'react-query';

import {
  FuelReceipt,
  fuelErrorMessage,
  fuelReadingsService,
} from '../../../services/fuelReadingsService';
import {
  formatDay,
  formatLiters,
  isoDay,
  normalizeDocument,
  parseLiters,
} from '../../../utils/fuelReadings';
import { PhotoInput } from './PhotoInput';

const BACKDATE_DAYS = 7;

// recebimento de diesel: quando o caminhão descarrega, o frentista anota a
// nf. a controladoria vê se a nota demorou para dar entrada no gescooper
// (enquanto não entra, o saldo do diesel no sistema fica errado).
export function ReceiptForm() {
  const queryClient = useQueryClient();
  const today = isoDay(new Date());
  const oldest = new Date();

  oldest.setDate(oldest.getDate() - BACKDATE_DAYS);

  const [document, setDocument] = useState('');
  const [liters, setLiters] = useState('');
  const [day, setDay] = useState(today);
  const [photo, setPhoto] = useState<Blob | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState<FuelReceipt | null>(null);

  const number = normalizeDocument(document);
  const amount = parseLiters(liters);
  const valid = !!number && Number.isFinite(amount) && amount > 0;

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await fuelReadingsService.createReceipt({
        document: number,
        liters: liters.trim(),
        received_on: day,
        photo,
        notes: notes.trim() || undefined,
      });

      setSaved(result);
      setDocument('');
      setLiters('');
      setDay(today);
      setPhoto(null);
      setNotes('');
      await queryClient.invalidateQueries('fuel-readings');
    } catch (err) {
      setError(fuelErrorMessage(err, 'Não foi possível salvar o recebimento.'));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <Stack spacing="4">
        <Box
          bg="green.50"
          color="green.900"
          borderWidth="1px"
          borderColor="green.300"
          borderRadius="12px"
          p="5"
          textAlign="center"
        >
          <Icon as={FiCheckCircle} boxSize="12" color="green.500" />
          <Text fontWeight="bold" fontSize="lg" mt="2">
            Recebimento registrado
          </Text>
          <Text>
            NF <b>{saved.document}</b>: {formatLiters(saved.liters)} L
          </Text>
          <Text fontSize="sm" mt="1">
            Descarga em {formatDay(saved.received_on)}
          </Text>
          <Text fontSize="sm" mt="3">
            A controladoria acompanha a entrada dessa nota no GesCooper.
          </Text>
        </Box>
        <Button size="lg" variant="outline" onClick={() => setSaved(null)}>
          Novo recebimento
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing="5">
      <Text fontSize="sm" color="gray.500">
        Quando o caminhão descarregar, anote a nota fiscal. Assim dá para ver se
        ela demorou para entrar no GesCooper.
      </Text>

      <Box>
        <Text fontWeight="medium" mb="2">
          1. Número da nota fiscal
        </Text>
        <Input
          value={document}
          onChange={event => setDocument(event.target.value)}
          inputMode="numeric"
          autoComplete="off"
          placeholder="Ex.: 105159"
          size="lg"
          fontSize="2xl"
          textAlign="center"
          fontFamily="mono"
          h="16"
          aria-label="Número da nota fiscal"
        />
      </Box>

      <Box>
        <Text fontWeight="medium" mb="2">
          2. Litros descarregados
        </Text>
        <Input
          value={liters}
          onChange={event => setLiters(event.target.value)}
          inputMode="decimal"
          autoComplete="off"
          placeholder="Ex.: 10.000"
          size="lg"
          fontSize="2xl"
          textAlign="center"
          fontFamily="mono"
          h="16"
          isInvalid={!!liters && !Number.isFinite(amount)}
          aria-label="Litros descarregados"
        />
        {Number.isFinite(amount) && (
          <Text fontSize="sm" mt="2" color="gray.500" textAlign="center">
            {formatLiters(amount)} litros
          </Text>
        )}
      </Box>

      <Box>
        <Text fontWeight="medium" mb="2">
          3. Dia da descarga
        </Text>
        <Input
          type="date"
          value={day}
          min={isoDay(oldest)}
          max={today}
          onChange={event => setDay(event.target.value || today)}
          size="lg"
          aria-label="Dia da descarga"
        />
        <Text fontSize="xs" mt="1" color="gray.500">
          Esqueceu de anotar? Dá para lançar até {BACKDATE_DAYS} dias depois.
        </Text>
      </Box>

      <Box>
        <Text fontWeight="medium" mb="2">
          4. Foto da nota (recomendado)
        </Text>
        <PhotoInput
          label="Tirar foto da nota fiscal"
          value={photo}
          onChange={setPhoto}
          onPreparing={setPreparing}
        />
      </Box>

      <Box>
        <Text fontSize="sm" mb="1" color="gray.500">
          Ocorrência (opcional)
        </Text>
        <Textarea
          value={notes}
          onChange={event => setNotes(event.target.value)}
          maxLength={500}
          rows={2}
          aria-label="Ocorrência do recebimento"
        />
      </Box>

      {error && (
        <Alert status="error" color="red.900" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Button
        colorScheme="blue"
        size="lg"
        h="14"
        onClick={submit}
        isLoading={saving}
        loadingText="Salvando..."
        isDisabled={!valid || preparing}
      >
        Salvar recebimento
      </Button>
    </Stack>
  );
}
